import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { BsYoutube } from 'react-icons/bs';
import { FiBookOpen } from 'react-icons/fi';
import Styled from '@/pages/company-management/company-options/option-view-style';
import {
  BodyText,
  Heading3Text,
  Subtitle2Text,
  Subtitle3Text,
} from '@/utils/global-fonts';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import { Col, Row } from '@/components/grid/grid-component';
import Card from '../card/card-component';
import theme from '@/common/theme';
import { toSentenceCase, truncateLongString } from '@/utils/utils';
import { SimpleImageGalleryView } from '../react-hook-form-inputs/image-gallery/image-gallery-component';
import Tooltip from '../tooltip/tooltip-component';

/**
 * OptionViewPage component displays detailed information about an option including
 * its properties, descriptions, media links, and image carousel.
 *
 * @param {object} props - The component props
 * @param {object} props.data - The option data object
 * @param {boolean} props.variant - Whether the data is a variant
 * @param {object} props.parts - The parts data object
 * @returns {JSX.Element} The rendered OptionViewPage component
 */
const OptionViewPage = ({ data, parts, variant }) => {
  // NEEDED FOR PARENT OPTION
  const findParent = variant
    ? null
    : data?.prices?.find((price) => price.is_default);

  const displayData = useMemo(() => {
    const thumbnailUrl = findParent?.thumbnail?.includes('http')
      ? findParent?.thumbnail
      : data?.option?.thumbnail;

    const gallery =
      findParent?.gallery?.length > 0
        ? findParent?.gallery
            ?.map((media) => ({
              image: media.image,
              alt: media?.image,
              embedUrl: null,
            }))
            .sort((a, b) => a.order - b.order)
        : [];

    const videoLink = variant
      ? data?.option?.video_link
      : findParent?.video_link;

    const priceId = findParent?.price_id ?? data?.option?.selectedPriceId;

    const findTempPn =
      parts?.find((part) => part.price_id === priceId)?.temp_pn ?? false;

    return {
      name: findParent?.name ?? data?.option?.name,
      part_number: findParent?.part_number ?? data?.option?.part_number,
      temp_pn: findTempPn,
      description: findParent?.description ?? data?.option?.description ?? null,
      quote_description:
        findParent?.quote_description ??
        data?.option?.quote_description ??
        null,
      brochure_link: findParent?.brochure_link ?? data?.option?.brochure_link,
      video_link: videoLink,
      media: gallery?.length ? gallery : data?.option?.media,
      thumbnail: thumbnailUrl ? `${thumbnailUrl}` : null,
      status: data?.option?.status,
    };
  }, [data, findParent, variant, parts]);

  const renderPartNumber = useCallback(() => {
    const { part_number: partNumber, temp_pn: tempPn } = displayData;

    const textContent = (
      <Subtitle3Text primary={!tempPn}>
        {toSentenceCase(partNumber)}
      </Subtitle3Text>
    );

    if (!tempPn) {
      return (
        <span key={`part-number-${data?.option?.id}-${partNumber}`}>
          {textContent}
        </span>
      );
    }

    return (
      <Tooltip
        key={`tooltip-${data?.option?.id}-${partNumber}`}
        id={`temp-pn-${data?.option?.id}-${partNumber}`}
        text={textContent}
        toolTipText="Temporary Part Number"
      />
    );
  }, [data, displayData]);

  if (!data) {
    return (
      <Card padding="16px 18px 10px 24px" margin="24px 0 0px 0px">
        <Styled.StyledRow>No data to display</Styled.StyledRow>
      </Card>
    );
  }

  return (
    <Row $margin="10px 0 0 0 !important">
      {/* Left Card - Content */}
      <Col
        xs={8}
        md={8}
        lg={8}
        $padding={
          variant
            ? '0px 24px 0px 24px !important'
            : '0px 15px 0px 0px !important'
        }
      >
        <Card padding="16px" margin="0 0 24px 0">
          {/* Header Row - Full Width */}
          <Styled.Header>
            <Styled.Span>
              <Heading3Text>{displayData?.name}</Heading3Text>
            </Styled.Span>
            <StatusIndicator status={displayData?.status} />
          </Styled.Header>

          <Styled.InfoColumnWrapper>
            <Row>
              <Col xs={12}>
                <Styled.DivStyle>
                  <Styled.Label color={theme.COLOR_GREY_TEXT}>
                    PART NUMBER
                  </Styled.Label>
                  <BodyText>{renderPartNumber()}</BodyText>
                </Styled.DivStyle>
              </Col>
            </Row>
            {displayData.description &&
              displayData.description !== '<p><br></p>' && (
                <Row>
                  <Col xs={12}>
                    <Styled.DivStyle>
                      <Styled.Label color={theme.COLOR_GREY_TEXT}>
                        FRONTEND DESCRIPTION
                      </Styled.Label>
                      <BodyText>
                        {parse(displayData.description ?? '')}
                      </BodyText>
                    </Styled.DivStyle>
                  </Col>
                </Row>
              )}
            {displayData.quote_description &&
              displayData.quote_description !== '<p><br></p>' && (
                <Row>
                  <Col xs={12}>
                    <Styled.DivStyle>
                      <Styled.Label color={theme.COLOR_GREY_TEXT}>
                        QUOTE DESCRIPTION
                      </Styled.Label>
                      <BodyText>
                        {parse(displayData.quote_description ?? '')}
                      </BodyText>
                    </Styled.DivStyle>
                  </Col>
                </Row>
              )}
            <Row padding="0 !important" margin="0 !important">
              <Col xs={12}>
                <Styled.LinkWrapper>
                  {displayData.video_link && (
                    <Styled.LinkContent>
                      <Styled.Label color={theme.COLOR_GREY_TEXT}>
                        VIDEO LINK
                      </Styled.Label>

                      <Styled.IconContainer>
                        <Styled.IconWrapper>
                          <BsYoutube size="14px" />
                        </Styled.IconWrapper>
                        <Styled.StyledLink
                          target="__blank"
                          to={displayData.video_link}
                        >
                          {truncateLongString(displayData.video_link ?? '', 45)}
                        </Styled.StyledLink>
                      </Styled.IconContainer>
                    </Styled.LinkContent>
                  )}

                  {displayData.brochure_link && (
                    <Styled.LinkContent>
                      <Styled.Label color={theme.COLOR_GREY_TEXT}>
                        BROCHURE LINK
                      </Styled.Label>
                      <Styled.IconContainer>
                        <Styled.IconWrapper>
                          <FiBookOpen size="14px" />
                        </Styled.IconWrapper>
                        <Styled.StyledLink
                          target="__blank"
                          to={displayData.brochure_link}
                        >
                          {truncateLongString(
                            displayData.brochure_link ?? '',
                            45,
                          )}
                        </Styled.StyledLink>
                      </Styled.IconContainer>
                    </Styled.LinkContent>
                  )}
                </Styled.LinkWrapper>
              </Col>
            </Row>
          </Styled.InfoColumnWrapper>
        </Card>
      </Col>

      {/* Right Column - Images */}
      <Col
        xs={4}
        md={4}
        lg={4}
        $padding={variant ? '0px 24px 0px 0px !important' : '0px !important'}
      >
        {/* Thumbnail Card */}

        <Card margin="0 0 15px 0">
          <Styled.Header>
            <Subtitle2Text>Thumbnail Image</Subtitle2Text>
          </Styled.Header>
          <Styled.ImageDiv>
            {displayData?.thumbnail ? (
              <Styled.ImageStyle src={displayData?.thumbnail} alt="" />
            ) : (
              <Styled.NoThumbnail />
            )}
          </Styled.ImageDiv>
        </Card>

        {/* Image Carousel Card */}
        {displayData?.media?.length > 0 && (
          <Card margin="0 0 24px 0">
            <Styled.Header>
              <Subtitle2Text>Product Gallery</Subtitle2Text>
            </Styled.Header>
            <SimpleImageGalleryView
              images={displayData?.media ?? []}
              margin="0"
            />
          </Card>
        )}
      </Col>
    </Row>
  );
};

OptionViewPage.propTypes = {
  data: PropTypes.object,
  variant: PropTypes.bool,
  parts: PropTypes.array,
};

OptionViewPage.defaultProps = {
  data: null,
  variant: false,
  parts: [],
};

export default OptionViewPage;
